import React from 'react';
import { Router } from '@uhg-abyss/web/ui/Router';
import { styled } from '@uhg-abyss/web/tools/styled';
import { createColumn, useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Card } from '@uhg-abyss/web/ui/Card';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { UI_KEYS } from '../../../common/uiKeys';
import { formatDisplayDateTime } from '../../../common/dateTime/formatDisplayDateTime';
import { batchJobsApi, type BatchRunHistoryItem } from './api/batchJobsApi';

const KEYS = UI_KEYS.ADMIN_UDW_BATCH_JOBS;

const Page = styled('section', {
  display: 'grid',
  gap: '1rem',
});

const Intro = styled('div', {
  display: 'grid',
  gap: '0.5rem',
});

const ActionGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '1rem',
});

const ActionCardBody = styled('div', {
  display: 'grid',
  gap: '0.75rem',
});

const HistoryCard = styled(Card, {
  display: 'grid',
  gap: '0.75rem',
});

const TableWrapper = styled('div', {
  width: '100%',
  boxSizing: 'border-box',
  minWidth: 0,
  borderRadius: '12px',
  border: '1px solid #c8d8e6',
  padding: '0',
  backgroundColor: '$white',
  '& .abyss-data-table-root': { width: '100%', minWidth: 0 },
  '& table': { width: '100%', tableLayout: 'auto' },
  '& th, & td': {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    verticalAlign: 'top',
  },
  '& .abyss-data-table-scroll': { overflowX: 'auto', overflowY: 'hidden' },
});

type AlertState = {
  status: 'success' | 'error';
  title: string;
  message: string;
} | null;

type HistoryRow = BatchRunHistoryItem & {
  uniqueId: string;
};

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message.trim()
    ? error.message
    : 'An unexpected error occurred while triggering the batch job.';
}

export const UdwBatchJobsPage = () => {
  const [loadingAction, setLoadingAction] = React.useState<'read' | 'push' | null>(null);
  const [alertState, setAlertState] = React.useState<AlertState>(null);
  const [history, setHistory] = React.useState<BatchRunHistoryItem[]>([]);
  const [historyError, setHistoryError] = React.useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = React.useState(true);

  const historyRows = React.useMemo<HistoryRow[]>(() => {
    return history.map((item) => {
      return {
        ...item,
        uniqueId: String(item.batchId),
      };
    });
  }, [history]);

  const historyColumns = React.useMemo(() => {
    return [
      createColumn<HistoryRow>({
        header: 'Job Type',
        accessorKey: 'jobType',
      }),
      createColumn<HistoryRow>({
        header: 'Source',
        accessorKey: 'source',
      }),
      createColumn<HistoryRow>({
        header: 'Processed Date',
        accessorKey: 'processedDate',
        cell: (context) => {
          return formatDisplayDateTime(context.getValue() as string | null, 'N/A');
        },
      }),
      createColumn<HistoryRow>({
        header: 'File Name',
        accessorKey: 'fileName',
        cell: (context) => {
          return context.getValue() ?? 'N/A';
        },
      }),
      createColumn<HistoryRow>({
        header: 'Total Records',
        accessorKey: 'totalRecords',
      }),
      createColumn<HistoryRow>({
        header: 'Status',
        accessorKey: 'status',
      }),
      createColumn<HistoryRow>({
        header: 'Error',
        accessorKey: 'error',
        cell: (context) => {
          return context.getValue() ?? 'None';
        },
      }),
    ];
  }, []);

  const historyTableState = useDataTable<HistoryRow>({
    initialData: historyRows,
    initialColumns: historyColumns,
  });

  React.useEffect(() => {
    historyTableState.setData(historyRows, true);
  }, [historyRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadHistory = React.useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await batchJobsApi.getRecentJobRuns();
      setHistory(result);
    } catch (error) {
      setHistoryError(getErrorMessage(error));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const runAction = React.useCallback(
    async (
      action: 'read' | 'push',
      request: () => Promise<string>,
      successTitle: string,
      successMessage: string,
      errorTitle: string,
    ) => {
      setLoadingAction(action);
      setAlertState(null);
      try {
        await request();
        await loadHistory();
        setAlertState({
          status: 'success',
          title: successTitle,
          message: successMessage,
        });
      } catch (error) {
        setAlertState({
          status: 'error',
          title: errorTitle,
          message: getErrorMessage(error),
        });
      } finally {
        setLoadingAction(null);
      }
    },
    [loadHistory]
  );

  return (
    <React.Fragment>
      <Router.MetaTags title="Batch Jobs" />
      <Page id={KEYS.DOM.PAGE} data-section-key={KEYS.SECTIONS.PAGE}>
      <Intro>
        <Heading level={1} size="lg">Batch Jobs</Heading>
        <Text asElement="p">
          Scheduled execution is the primary path for UDW batch jobs. Use this admin page as a
          backup/manual trigger surface when operations staff need a controlled rerun or a
          scheduled run has failed.
        </Text>
      </Intro>

      {alertState ? (
        <div id={KEYS.DOM.ALERT} data-section-key={KEYS.SECTIONS.FEEDBACK}>
          <Alert
            ariaText={alertState.status === 'success' ? 'Success' : 'Error'}
            title={alertState.title}
            status={alertState.status}
            isVisible
            onClose={() => {return setAlertState(null)}}
          >
            {alertState.message}
          </Alert>
        </div>
      ) : null}

      <ActionGrid id={KEYS.DOM.ACTIONS} data-section-key={KEYS.SECTIONS.ACTIONS}>
        <Card id={KEYS.DOM.READ_CARD}>
          <ActionCardBody>
            <Heading level={2} size="sm">Read UDW Data From NAS Drive</Heading>
            <Text asElement="p">
              Copy policy data files from the configured UDW/NAS location, validate the header,
              and update policy records plus batch-log entries.
            </Text>
            <Button
              id={KEYS.DOM.READ_BUTTON}
              variant="filled"
              color="brand"
              isLoading={loadingAction === 'read'}
              ariaLoadingLabel="Triggering read UDW batch"
              onClick={() => {
                return void runAction(
                  'read',
                  batchJobsApi.triggerReadUdwDataFromNasDrive,
                  'Manual read trigger submitted',
                  'The manual UDW read trigger was accepted and forwarded for background execution.',
                  'Unable to trigger read batch'
                );
              }}
            >
              Read UDW Data From NAS Drive
            </Button>
          </ActionCardBody>
        </Card>

        <Card id={KEYS.DOM.PUSH_CARD}>
          <ActionCardBody>
            <Heading level={2} size="sm">Push RFI Data To NAS Drive</Heading>
            <Text asElement="p">
              Export prior-year completed RFI responses for the active survey and publish the data
              and trigger files to the configured UDW/NAS destination.
            </Text>
            <Button
              id={KEYS.DOM.PUSH_BUTTON}
              variant="outline"
              color="neutral"
              isLoading={loadingAction === 'push'}
              ariaLoadingLabel="Triggering push RFI batch"
              onClick={() => {
                return void runAction(
                  'push',
                  batchJobsApi.triggerPushRfiDataToNasDrive,
                  'Manual push trigger submitted',
                  'The manual RFI export trigger was accepted and forwarded for background execution.',
                  'Unable to trigger push batch'
                );
              }}
            >
              Push RFI Data To NAS Drive
            </Button>
          </ActionCardBody>
        </Card>
      </ActionGrid>

      <HistoryCard id={KEYS.DOM.HISTORY} data-section-key={KEYS.SECTIONS.HISTORY}>
        <Heading level={2} size="sm">Latest 7 Job Runs</Heading>
        <Text asElement="p">
          Recent UDW batch run history from `udw_batch_log`, combining scheduled and manual runs.
        </Text>

        {historyLoading ? (
          <Text asElement="p">Loading recent batch history...</Text>
        ) : historyError ? (
          <Text asElement="p">{historyError}</Text>
        ) : historyRows.length === 0 ? (
          <Text asElement="p">No recent batch runs were found.</Text>
        ) : (
          <TableWrapper id={KEYS.DOM.HISTORY_TABLE}>
            <DataTable<HistoryRow> title="Latest 7 Job Runs" tableState={historyTableState}>
              <DataTable.Table />
            </DataTable>
          </TableWrapper>
        )}
      </HistoryCard>
      </Page>
    </React.Fragment>
  );
};